/**
 * Utility functions for pace calculations
 */

/**
 * Calculate pace in minutes per unit (mile or kilometer)
 */
export const calculatePace = (
  timeMs: number,
  distanceMeters: number,
  unit: 'mile' | 'km' = 'mile'
): number => {
  // --- Start: Enhanced Validation ---
  if (
    typeof timeMs !== 'number' ||
    typeof distanceMeters !== 'number' || // Type check
    !isFinite(timeMs) ||
    !isFinite(distanceMeters) || // Finite check
    timeMs <= 0 ||
    distanceMeters <= 0 // Non-positive check
  ) {
    // Add logging to understand why pace calculation failed
    // Avoid logging excessively in production if this is common
    // console.warn(`Invalid input for calculatePace: timeMs=${timeMs}, distanceMeters=${distanceMeters}. Returning 0.`);
    return 0; // Return 0 for invalid inputs
  }
  // --- End: Enhanced Validation ---

  // Convert milliseconds to minutes
  const timeMinutes = timeMs / 60000;

  // Convert meters to specified unit
  const conversionFactor = unit === 'mile' ? 1609.34 : 1000; // 1 mile = 1609.34m, 1 km = 1000m
  const distance = distanceMeters / conversionFactor;

  // Check distance after conversion as well
  if (distance <= 0 || !isFinite(distance)) {
    // console.warn(`Invalid calculated distance for calculatePace: distance=${distance}. Returning 0.`);
    return 0;
  }

  // Calculate pace in minutes per unit
  const pace = timeMinutes / distance;

  // --- Start: Added Final Check ---
  // Ensure result is finite and reasonable (e.g., less than 60 min/unit)
  if (!isFinite(pace) || pace >= 60) {
    // console.warn(`Unreasonable pace calculated: ${pace} min/${unit}. Returning 0.`);
    return 0;
  }
  // --- End: Added Final Check ---

  return pace;
};

/**
 * Convert pace between miles and kilometers
 */
export const convertPace = (
  pace: number | null | undefined,
  fromUnit: 'mile' | 'km',
  toUnit: 'mile' | 'km'
): number | null => {
  // Handle invalid inputs
  if (pace === null || pace === undefined || !isFinite(pace) || pace <= 0) {
    return null;
  }

  // No conversion needed if units are the same
  if (fromUnit === toUnit) return pace;

  // Make sure we have valid units
  const validFrom = fromUnit === 'mile' || fromUnit === 'km';
  const validTo = toUnit === 'mile' || toUnit === 'km';

  if (!validFrom || !validTo) {
    return null;
  }

  // Convert from minutes/mile to minutes/kilometer or vice versa
  // minutes/mile ÷ 1.609344 = minutes/km
  // minutes/km × 1.609344 = minutes/mile
  const conversionFactor = 1.609344;
  let result: number;

  if (fromUnit === 'mile' && toUnit === 'km') {
    // Converting from min/mi to min/km (value gets smaller)
    result = pace / conversionFactor;
  } else {
    // Converting from min/km to min/mi (value gets larger)
    result = pace * conversionFactor;
  }

  // Make sure the result is finite and reasonable
  return isFinite(result) ? result : null;
};

/**
 * Calculate overall pace from total time and distance
 */
export const calculateOverallPace = (
  totalTimeMs: number,
  totalDistanceMeters: number,
  unit: 'mile' | 'km' = 'mile'
): number => {
  return calculatePace(totalTimeMs, totalDistanceMeters, unit);
};

/**
 * Interface for segment objects
 */
interface Segment {
  time: number;
  distance: number;
}

/**
 * Calculate average pace for multiple segments
 */
export const calculateAveragePace = (
  segments: Segment[] | null | undefined,
  unit: 'mile' | 'km' = 'mile'
): number => {
  if (!segments || segments.length === 0) return 0;

  const totalTime = segments.reduce((sum, segment) => sum + segment.time, 0);
  const totalDistance = segments.reduce((sum, segment) => sum + segment.distance, 0);

  return calculatePace(totalTime, totalDistance, unit);
};
